import { Activity } from '../../../types/activities'
import { PagePanelColors } from '../../../types/page-panel'
import { Question, QuestionsFormData, QuestionTypes } from '../../../types/questions'
import { CloseIcon, DictionaryText } from '../../../../MyStichtingOpenComponents/src'
import { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { Control, FieldValues } from 'react-hook-form'
import CheckboxQuestion from '../../form/question-types/CheckboxQuestion'
import FillInTheBlanks from '../../form/question-types/FillInTheBlanks'
import RadioQuestion from '../../form/question-types/RadioQuestion'
import PagePanel from '../../layout/PagePanel'
import { createFormKeyFromActivityAndQuestion } from '../../../helpers/registration-check'
import { AdditionalQuestionInformation } from './_partials/AdditionalQuestionInformation'
import { EndpointNotificationList } from './_partials/EndpointNotificationList'
import { RegistrationCheckContext } from '../../../providers/registration-check-provider'
import { getActivityEndpoints } from './_helpers/getActivityEndpoints'

type Props = {
    question: Question
    questions: Question[]
    activity: Activity
    visibleQuestions: string[]
    control: Control<FieldValues, any>
    formData: QuestionsFormData
    canBeDeleted: boolean
    deleteActivity: () => void
    updateActivity: (formData: QuestionsFormData) => void
}

const MESSAGE_KEYS = { REMOVE: 'STO.COMMON.ACTIONS.REMOVE' }

export const QuestionItem = ({
    question,
    questions,
    activity,
    visibleQuestions,
    control,
    formData,
    canBeDeleted,
    deleteActivity,
    updateActivity,
}: Props): ReactElement => {
    const [isVisible, setVisibility] = useState(false)
    const [isQuestionAnswered, setIsQuestionAnswered] = useState(false)
    const [isInfoModalVisible, setInfoModalVisibility] = useState(false)
    const element = useRef(null)
    const activityAndQuestionCombinationKey = createFormKeyFromActivityAndQuestion(activity, question)

    const getPagePanelColor = (): PagePanelColors =>
        !isQuestionAnswered || !element.current?.nextSibling ? PagePanelColors.ACCENT : PagePanelColors.DEFAULT

    useEffect(() => {
        setVisibility(visibleQuestions.includes(question.id))
        updateActivity(formData)
    }, [question, visibleQuestions, setVisibility])

    useEffect(() => {
        const matchingFormData = formData[activityAndQuestionCombinationKey]
        setIsQuestionAnswered(matchingFormData && matchingFormData !== '')
    }, [formData])

    const { endpointsPerFormKey, setEndpointsPerFormKey } = useContext(RegistrationCheckContext)

    /**
     * Updates the list of endpoints for a specific activity and question combination,
     * merging new endpoints with existing ones and removing obsolete entries
     */
    const updateEndpointsList = () => {
        const newEndpoints = getActivityEndpoints(formData, activityAndQuestionCombinationKey, question)
        const existingEndpoints = endpointsPerFormKey[activityAndQuestionCombinationKey]

        if (!existingEndpoints) {
            setEndpointsPerFormKey(activityAndQuestionCombinationKey, newEndpoints)
            return
        }

        // Filter newEndpoints to only include those with endpointKeys not in existingEndpoints
        const uniqueNewEndpoints = newEndpoints.filter(
            (newEndpoint) => !existingEndpoints.some(({ endpointKey }) => endpointKey === newEndpoint.endpointKey),
        )

        //filter out the endpoints that have been removed
        const remainingExistingEndpoints = existingEndpoints.filter(({ endpointKey }) =>
            newEndpoints.some((newEndpoint) => newEndpoint.endpointKey === endpointKey),
        )

        setEndpointsPerFormKey(activityAndQuestionCombinationKey, [
            ...remainingExistingEndpoints,
            ...uniqueNewEndpoints,
        ])
    }

    useEffect(() => {
        updateEndpointsList()
    }, [activity])

    return (
        <>
            {isVisible && (
                <div data-cy="question-row">
                    <PagePanel
                        ref={element}
                        color={getPagePanelColor()}
                        onClickMoreInfo={
                            question.additionalInformation || canBeDeleted
                                ? () => {
                                      canBeDeleted ? deleteActivity() : setInfoModalVisibility(true)
                                  }
                                : null
                        }
                        moreInfoText={canBeDeleted ? <DictionaryText textKey={MESSAGE_KEYS.REMOVE} /> : null}
                        moreInfoIcon={canBeDeleted ? <CloseIcon /> : null}
                        containerClasses="flex gap-2 items-center flex-wrap"
                        className={`question ${isQuestionAnswered ? 'question--answered' : 'question--unanswered'}`}
                    >
                        {question.questionType === QuestionTypes.FILL_IN_THE_BLANKS && (
                            <FillInTheBlanks
                                question={question}
                                questions={questions}
                                activity={activity}
                                control={control}
                                formData={formData}
                            />
                        )}
                        {[QuestionTypes.INLINE_RADIO, QuestionTypes.RADIO].includes(question.questionType) && (
                            <RadioQuestion
                                question={question}
                                name={activityAndQuestionCombinationKey}
                                control={control}
                                inline={question.questionType === QuestionTypes.INLINE_RADIO}
                            />
                        )}
                        {[QuestionTypes.INLINE_CHECKBOX, QuestionTypes.CHECKBOX].includes(question.questionType) && (
                            <CheckboxQuestion
                                question={question}
                                name={activityAndQuestionCombinationKey}
                                control={control}
                                inline={question.questionType === QuestionTypes.INLINE_CHECKBOX}
                            />
                        )}
                    </PagePanel>
                    <AdditionalQuestionInformation
                        question={question}
                        isInfoModalVisible={isInfoModalVisible}
                        setInfoModalVisibility={setInfoModalVisibility}
                    />
                </div>
            )}
            {isQuestionAnswered && (
                <EndpointNotificationList
                    activityAndQuestionCombinationKey={activityAndQuestionCombinationKey}
                    endpoints={endpointsPerFormKey[activityAndQuestionCombinationKey]}
                />
            )}
        </>
    )
}
